import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight, Factory, Globe2, BadgeDollarSign, Zap, Ship, ShieldCheck,
  ChevronLeft, ChevronRight, Star, Quote, CheckCircle2,
} from "lucide-react";
import {
  industries, factory,
} from "../mock/mockData";
import { useContent } from "../context/ContentContext";
import { fetchProducts } from "../services/api";
import { Button } from "../components/ui/button";
import ProductCard from "../components/ProductCard";

const iconMap = { Factory, Globe2, BadgeDollarSign, Zap, Ship, ShieldCheck };

const Home = () => {
  const [slide, setSlide] = useState(0);
  const [featured, setFeatured] = useState([]);
  const navigate = useNavigate();
  const { content, categories } = useContent();
  const heroSlides = content.hero;
  const whyChooseUs = content.whyChooseUs;
  const stats = content.stats;
  const catSection = content.categoriesSection;
  const facSection = content.factorySection;
  const projSection = content.projectsSection;
  const testSection = content.testimonialsSection;
  const featuredProjects = projSection.items || [];
  const testimonials = testSection.items || [];

  useEffect(() => {
    const t = setInterval(() => setSlide((s) => (s + 1) % heroSlides.length), 6000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    fetchProducts().then((all) => {
      const best = all.filter((p) => (p.tags || []).includes("Best Seller")).slice(0, 4);
      setFeatured(best.length >= 4 ? best : all.slice(0, 8));
    }).catch(() => {});
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative h-[88vh] min-h-[560px] overflow-hidden bg-[#14171c]">
        {heroSlides.map((s, i) => (
          <div key={s.id} className={`absolute inset-0 transition-opacity duration-1000 ${i === slide ? "opacity-100" : "opacity-0"}`}>
            <img src={s.image} alt={s.title} className={`w-full h-full object-cover ${i === slide ? "animate-kenburns" : ""}`} />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0e1116]/96 via-[#0e1116]/82 to-[#0e1116]/45" />
            <div className="absolute inset-0 bg-[#0e1116]/25" />
          </div>
        ))}
        <div className="relative z-10 max-w-7xl mx-auto px-6 h-full flex items-center">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-[#C9A227]/15 border border-[#C9A227]/40 text-[#E7C766] text-xs font-semibold px-4 py-1.5 rounded-full mb-6 animate-fade-up">
              <span className="w-1.5 h-1.5 rounded-full bg-[#C9A227]" /> Factory Direct Production in China
            </div>
            <h1 key={slide} className="font-display font-extrabold text-white text-4xl md:text-6xl leading-[1.05] animate-fade-up">
              {heroSlides[slide].title} <span className="text-gold-gradient">{heroSlides[slide].highlight}</span>
            </h1>
            <p className="text-neutral-300 text-lg mt-6 max-w-xl animate-fade-up">{heroSlides[slide].subtitle}</p>
            <div className="flex flex-wrap gap-4 mt-8 animate-fade-up">
              <Button onClick={() => navigate("/contact")} className="bg-[#C9A227] hover:bg-[#A6841B] text-white font-semibold rounded-full px-7 text-base h-12">
                Request a Quote <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
              <Button onClick={() => navigate("/category/medals-awards")} variant="outline" className="bg-white/5 border-white/30 text-white hover:bg-white hover:text-[#14171c] rounded-full h-12 px-7 text-base">
                View Products
              </Button>
            </div>
          </div>
        </div>
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex items-center gap-4">
          <button onClick={() => setSlide((s) => (s - 1 + heroSlides.length) % heroSlides.length)} className="w-10 h-10 rounded-full border border-white/30 text-white flex items-center justify-center hover:bg-white hover:text-[#14171c] transition-colors"><ChevronLeft className="w-5 h-5" /></button>
          <div className="flex gap-2">
            {heroSlides.map((_, i) => (
              <button key={i} onClick={() => setSlide(i)} className={`h-1.5 rounded-full transition-all ${i === slide ? "w-8 bg-[#C9A227]" : "w-4 bg-white/40"}`} />
            ))}
          </div>
          <button onClick={() => setSlide((s) => (s + 1) % heroSlides.length)} className="w-10 h-10 rounded-full border border-white/30 text-white flex items-center justify-center hover:bg-white hover:text-[#14171c] transition-colors"><ChevronRight className="w-5 h-5" /></button>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="py-20 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="text-[#C9A227] font-bold text-sm uppercase tracking-widest">Why Choose Us</span>
            <h2 className="font-display font-extrabold text-3xl md:text-4xl text-[#14171c] mt-3">A Real Manufacturer, Not a Trading Company</h2>
            <div className="gold-rule mx-auto mt-5" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyChooseUs.map((w) => {
              const Icon = iconMap[w.icon];
              return (
                <div key={w.title} className="group p-8 rounded-2xl border border-neutral-100 bg-white card-lift">
                  <div className="w-14 h-14 rounded-xl bg-[#C9A227]/10 flex items-center justify-center mb-5 group-hover:bg-[#C9A227] transition-colors">
                    <Icon className="w-7 h-7 text-[#C9A227] group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="font-display font-bold text-lg text-[#14171c] mb-2">{w.title}</h3>
                  <p className="text-neutral-600 text-[15px] leading-relaxed">{w.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="py-20 md:py-24 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="text-[#C9A227] font-bold text-sm uppercase tracking-widest">{catSection.eyebrow}</span>
            <h2 className="font-display font-extrabold text-3xl md:text-4xl text-[#14171c] mt-3">{catSection.title}</h2>
            <div className="gold-rule mx-auto mt-5" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {categories.map((c) => (
              <Link key={c.slug} to={`/category/${c.slug}`} className="group relative rounded-2xl overflow-hidden h-72 card-lift">
                <img src={c.image} alt={c.name} className="w-full h-full object-cover img-zoom" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0e1116] via-[#0e1116]/40 to-transparent" />
                <div className="absolute bottom-0 left-0 p-7">
                  <div className="text-[#E7C766] text-xs font-bold uppercase tracking-widest mb-1">{c.tagline}</div>
                  <h3 className="font-display font-extrabold text-2xl text-white mb-2">{c.name}</h3>
                  <span className="inline-flex items-center gap-1.5 text-white text-sm font-semibold opacity-0 group-hover:opacity-100 -translate-y-1 group-hover:translate-y-0 transition-all">View Products <ArrowRight className="w-4 h-4" /></span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="py-20 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
            <div>
              <span className="text-[#C9A227] font-bold text-sm uppercase tracking-widest">Best Sellers</span>
              <h2 className="font-display font-extrabold text-3xl md:text-4xl text-[#14171c] mt-3">Popular Products</h2>
            </div>
            <Link to="/category/medals-awards">
              <Button variant="outline" className="rounded-full border-neutral-300 hover:border-[#C9A227] hover:text-[#C9A227]">View All <ArrowRight className="w-4 h-4 ml-1" /></Button>
            </Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {featured.map((p) => (<ProductCard key={p.id} product={p} />))}
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="py-20 md:py-24 bg-ink-gradient text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="text-[#E7C766] font-bold text-sm uppercase tracking-widest">Industries We Serve</span>
            <h2 className="font-display font-extrabold text-3xl md:text-4xl mt-3">Trusted Across the Sporting World</h2>
            <div className="gold-rule mx-auto mt-5" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {industries.map((ind) => (
              <div key={ind} className="flex items-center gap-3 p-4 rounded-xl bg-white/5 border border-white/10 hover:border-[#C9A227]/50 hover:bg-white/10 transition-colors">
                <CheckCircle2 className="w-5 h-5 text-[#C9A227] flex-shrink-0" />
                <span className="text-sm font-medium text-neutral-200">{ind}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FACTORY TEASER */}
      <section className="py-20 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
          <div className="grid grid-cols-2 gap-4">
            {facSection.gallery.slice(0, 4).map((img, i) => (
              <div key={i} className={`rounded-2xl overflow-hidden ${i % 2 === 0 ? "mt-0" : "mt-8"}`}>
                <img src={img} alt="Factory" className="w-full h-52 object-cover" />
              </div>
            ))}
          </div>
          <div>
            <span className="text-[#C9A227] font-bold text-sm uppercase tracking-widest">{facSection.eyebrow}</span>
            <h2 className="font-display font-extrabold text-3xl md:text-4xl text-[#14171c] mt-3 mb-5">{facSection.title}</h2>
            <p className="text-neutral-600 leading-relaxed mb-6">{facSection.text}</p>
            <div className="grid grid-cols-2 gap-5 mb-8">
              {stats.map((s) => (
                <div key={s.label}>
                  <div className="font-display font-extrabold text-3xl text-gold-gradient">{s.value}</div>
                  <div className="text-sm text-neutral-500 mt-1">{s.label}</div>
                </div>
              ))}
            </div>
            <Link to="/factory"><Button className="bg-[#14171c] hover:bg-[#000] text-white rounded-full h-12 px-7">Visit Our Factory <ArrowRight className="w-4 h-4 ml-1" /></Button></Link>
          </div>
        </div>
      </section>

      {/* FEATURED PROJECTS */}
      <section className="py-20 md:py-24 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="text-[#C9A227] font-bold text-sm uppercase tracking-widest">{projSection.eyebrow}</span>
            <h2 className="font-display font-extrabold text-3xl md:text-4xl text-[#14171c] mt-3">{projSection.title}</h2>
            <div className="gold-rule mx-auto mt-5" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featuredProjects.map((pr) => (
              <div key={pr.title} className="group bg-white rounded-2xl overflow-hidden border border-neutral-100 card-lift">
                <div className="relative h-56 overflow-hidden">
                  <img src={pr.image} alt={pr.title} className="w-full h-full object-cover img-zoom" />
                  <span className="absolute top-4 left-4 bg-white/90 text-[#14171c] text-xs font-bold px-3 py-1 rounded-full">{pr.category}</span>
                </div>
                <div className="p-6">
                  <h3 className="font-display font-bold text-lg text-[#14171c] mb-2">{pr.title}</h3>
                  <p className="text-neutral-600 text-sm leading-relaxed">{pr.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-20 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="text-[#C9A227] font-bold text-sm uppercase tracking-widest">{testSection.eyebrow}</span>
            <h2 className="font-display font-extrabold text-3xl md:text-4xl text-[#14171c] mt-3">{testSection.title}</h2>
            <div className="gold-rule mx-auto mt-5" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {testimonials.map((t) => (
              <div key={t.name} className="p-7 rounded-2xl border border-neutral-100 bg-neutral-50 relative">
                <Quote className="w-9 h-9 text-[#C9A227]/25 mb-3" />
                <div className="flex gap-0.5 mb-3">{Array.from({ length: t.rating }).map((_, i) => (<Star key={i} className="w-4 h-4 fill-[#C9A227] text-[#C9A227]" />))}</div>
                <p className="text-neutral-700 text-[15px] leading-relaxed mb-5">"{t.text}"</p>
                <div><div className="font-display font-bold text-[#14171c]">{t.name}</div><div className="text-sm text-neutral-500">{t.role}</div></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative py-24 overflow-hidden bg-[#14171c]">
        <img src="https://images.unsplash.com/photo-1706374503312-7a4a4c030d2d" alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-display font-extrabold text-3xl md:text-5xl text-white leading-tight">Tell Us About Your Project & Receive a <span className="text-gold-gradient">Custom Quotation</span></h2>
          <p className="text-neutral-300 mt-5 text-lg">Factory-direct pricing, fast sampling and worldwide shipping — with European-level service and communication.</p>
          <div className="flex flex-wrap justify-center gap-4 mt-9">
            <Link to="/contact"><Button className="bg-[#C9A227] hover:bg-[#A6841B] text-white font-semibold rounded-full px-8 text-base h-12">Request a Quote <ArrowRight className="w-4 h-4 ml-1" /></Button></Link>
            <Link to="/gallery"><Button variant="outline" className="bg-transparent border-white/30 text-white hover:bg-white hover:text-[#14171c] rounded-full h-12 px-8 text-base">View Gallery</Button></Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
